import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import adminService from "../../api/services/adminService";
import { confirmDanger } from "../../utils/confirmDialog";
import { FormSkeleton } from "../common/skeletons";

const inputClass =
  "w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700 min-w-0";

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return name || user?.email || user?.contactNo || `User #${user?.id}`;
}

export default function ExpirePremiumSection({ onSuccess, embedded = false }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim() || selectedUser) {
      setSearchResults([]);
      setShowDropdown(false);
      return undefined;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminService.searchUsersForSubscription(searchQuery.trim());
        setSearchResults(res?.users || []);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, selectedUser]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(displayName(user));
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleExpire = async () => {
    const userId = selectedUser?.id;
    if (!userId) {
      toast.error("Select a user first");
      return;
    }
    const ok = await confirmDanger(
      `Expire premium for ${displayName(selectedUser)} (ID ${userId})? They will lose premium access immediately on web and mobile.`,
      "Expire user premium?"
    );
    if (!ok) return;

    setSubmitting(true);
    try {
      const res = await adminService.expireUserPremium(userId);
      if (res?.success) {
        toast.success(res?.message || "Premium expired");
        setSelectedUser((prev) =>
          prev ? { ...prev, isPremium: res?.isPremium ? 1 : 0 } : prev
        );
        onSuccess?.();
      } else {
        toast.error(res?.message || "Failed to expire premium");
      }
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.message || "Failed to expire premium");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        embedded
          ? "overflow-hidden"
          : "bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6"
      }
    >
      {!embedded ? (
        <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">Expire user premium</h2>
          <p className="mt-1 text-sm text-gray-500">
            Admin or Developer: end all active subscriptions for a user. Their app and web session update live.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-4">
          End all active subscriptions for a user. Their app and web session update live.
        </p>
      )}
      <div className={embedded ? "" : "p-5 sm:p-6"}>
        <div className="space-y-4 max-w-2xl">
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search user (name, email, phone, or ID)
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSelectedUser(null);
                setSearchQuery(e.target.value);
              }}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Type to search..."
              className={inputClass}
            />
            {selectedUser ? (
              <button
                type="button"
                onClick={handleClearUser}
                className="absolute right-2 top-9 text-gray-400 hover:text-red-600 text-sm"
              >
                Clear
              </button>
            ) : null}
            {searching ? <FormSkeleton fields={2} /> : null}
            {showDropdown && searchResults.length > 0 ? (
              <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                {searchResults.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    onClick={() => handleSelectUser(u)}
                  >
                    <span className="font-medium text-gray-900">{displayName(u)}</span>
                    <span className="block text-xs text-gray-500">
                      ID {u.id}
                      {u.email ? ` · ${u.email}` : ""}
                      {u.isPremium === 1 || u.isPremium === true ? " · Premium" : " · Free"}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {selectedUser ? (
            <p className="text-sm text-gray-700">
              Selected: <span className="font-semibold">{displayName(selectedUser)}</span>{" "}
              <span className="text-gray-400">(ID {selectedUser.id})</span>
              <span
                className={`ml-2 text-xs font-semibold ${
                  selectedUser.isPremium === 1 || selectedUser.isPremium === true
                    ? "text-emerald-700"
                    : "text-gray-500"
                }`}
              >
                {selectedUser.isPremium === 1 || selectedUser.isPremium === true ? "Premium" : "Free"}
              </span>
            </p>
          ) : null}

          <button
            type="button"
            disabled={!selectedUser?.id || submitting}
            onClick={handleExpire}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
          >
            {submitting ? "Expiring…" : "Expire premium now"}
          </button>
        </div>
      </div>
    </div>
  );
}
